import { cn } from '@/lib/utils'
import type { VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import type { toggleVariants } from './ui'
import { ToggleGroup, ToggleGroupItem } from './ui'

export interface ToggleOption<TValue extends string> {
  value: TValue
  label: ReactNode

  badge?: number | null
  hide?: boolean
}

interface Props<TValue extends string>
  extends VariantProps<typeof toggleVariants> {
  className?: string

  value: TValue
  options: ToggleOption<TValue>[]

  wide?: boolean
  clearable?: boolean

  onChange(value: TValue): void
}

export function Toggles<TValue extends string>({
  className,

  value,
  options,

  variant = 'outline',
  size,

  wide = false,
  clearable = false,

  onChange,
}: Props<TValue>) {
  function handleChange(val: TValue) {
    if (!clearable && !val) return
    onChange(val)
  }

  return (
    <ToggleGroup
      className={cn(wide && 'w-full grid grid-flow-col', className)}
      type="single"
      value={value}
      variant={variant}
      size={size}
      onValueChange={handleChange}
    >
      {options.map(toggle =>
        !!toggle.hide ? null : (
          <ToggleGroupItem
            key={toggle.value}
            className="relative"
            value={toggle.value}
          >
            <span className="translate-y-px">{toggle.label}</span>

            {typeof toggle.badge === 'number' && (
              <div
                className={cn(
                  'absolute top-0 right-0 mt-1 mr-1 flex py-0.5 px-1 items-center justify-center',
                  'rounded-full corner-squircle bg-red-600 text-[0.6rem] font-semibold text-white'
                )}
              >
                {toggle.badge}
              </div>
            )}
          </ToggleGroupItem>
        )
      )}
    </ToggleGroup>
  )
}
